import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { map, filter, reduce, findIndex, cloneDeep } from 'lodash';
import { Link } from 'react-router-dom';
import ReactResizeDetector from 'react-resize-detector';
import { AgGridReact } from 'ag-grid-react';

import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import ProgressBar from '../../../../components/progress-bar';

import SearchField from '../../../../components/search-field';
// Import Actions
import { requestFetchDealTypeFields,
  requestUpdateDealTypeFields } from '../../SetupActions';

// Import Selectors
import { getAllDealTypeFields } from '../../SetupReducer';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { getViewableDimensions } from '../../../App/AppReducer';
import DataTypeCellRenderer from '../../components/cells/DataTypeCellRenderer';
import ToggleCellRenderer from '../../components/cells/ToggleFieldRenderer';
import APICellRenderer from '../../components/cells/APICellRenderer';
import FieldLabelCellRenderer from '../../components/cells/FieldLabelCellRenderer';
import CRMInstanceName from '../../../../components/crm-instance-name';

class DealTypePage extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      loaded: false,
      error: null,
      allUpdating: false,
      fields: [],
      dirty: false,
      navBarHeight: 0,
    };
    this.onGridReady = this.onGridReady.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.loadCustomFields = this.loadCustomFields.bind(this);
    this.markAsTypeField = this.markAsTypeField.bind(this);
    this.submit = this.submit.bind(this);
    this.onResizNavBar = this.onResizNavBar.bind(this);
  }

  componentDidMount() {
    this.loadCustomFields();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  onResizNavBar(width, navBarHeight) {
    this.setState({ navBarHeight });
  }

  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }

  loadCustomFields() {
    this.setState({
      loading: true, error: null, loaded: false, fields: [],
    });
    const allFieldsPromise = this.props.dispatch(requestFetchDealTypeFields());
    allFieldsPromise.then(
      (results) => {
        // clone!!!!! results.fields
        const fields = reduce(results.fields || [], (items, field) => {
          if (field.sync_required === true || field.is_type_field === true) {
            const f = cloneDeep(field);
            if (f.name === 'Type') {
              f.is_default = true;
            }
            f.original_is_type_field = f.is_type_field;
            items.push(f);
          }
          return items;
        }, []);
        this.setState({
          loading: false, error: null, loaded: true, fields,
        });
      },
      (error) => {
        this.setState({
          loading: false, error, loaded: false, fields: [],
        });
      },
    );
    return allFieldsPromise;
  }

  markAsTypeField(field) {
    const { fields } = this.state;
    if (field) {
      field.is_type_field = !field.is_type_field; // toggle
      const idx = findIndex(fields, { _id: field._id });
      if (idx >= 0) {
        const newFields = [...fields];
        newFields[idx] = field;
        this.setState({ fields: newFields });
      }  
    }
    const dirtyFields = filter(fields, f => f.original_is_type_field !== f.is_type_field);
    const dirty = dirtyFields.length > 0;
    this.setState({ dirty }, () => {
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
    });
  }

  submit() {
    const { fields, allUpdating } = this.state;
    if (allUpdating) {
      return;
    }
    this.setState({ allUpdating: true });
    const selectedFields = map(filter(fields, (f) => {
      return f.is_type_field === true && f.accessible !== false;
    }), 'name');
    this.props.dispatch(requestUpdateDealTypeFields({ type_fields: selectedFields }))
      .then(
        () => {
          this.setState({ allUpdating: false });
        },
        () => {
          this.setState({ allUpdating: false });
        },
      );
  }


  renderTable() {
    const { viewableDimensions } = this.props;
    const {
      fields, allUpdating, dirty,
      navBarHeight,
    } = this.state;

    if (fields.length === 0) {
      return (
        <div className="alert alert-danger">
          No custom fields of type <code>currency</code>, <code>number</code> nor <code>formula</code> found.
        </div>
      );
    }

    const viewableHeight = viewableDimensions.height - navBarHeight;

    const columnDefs = [
      {
        headerName: 'Field Name',
        field: 'label',
        suppressMenu: true,
        width: 450,
        suppressMovable: true,
        cellRenderer: FieldLabelCellRenderer,
        pinned: 'left',
      },
      {
        headerName: 'API Name',
        field: 'name',
        suppressMenu: true,
        width: 345,
        cellRenderer: APICellRenderer,
        suppressMovable: true,
      },
      {
        headerName: 'Data Type',
        field: 'type',
        suppressMenu: true,
        width: 200,
        suppressMovable: true,
        cellRenderer: DataTypeCellRenderer,
      },
      {
        headerName: 'Use as Type Field',
        field: 'is_type_field',
        suppressMenu: true,
        width: 200,
        suppressMovable: true,
        cellRenderer: ToggleCellRenderer,
        cellRendererParams: {
          onToggle: this.markAsTypeField,
          disabled: allUpdating,
        },
      },
    ];

    return (
      <div>
        {fields.length > 0 && 
          <nav className="navbar navbar-default navbar-controls">
            <form className="navbar-form navbar-left">
              <SearchField collapsible={false} onSearch={this.handleSearch} />
            </form>
            {dirty &&
              <div className="navbar-form navbar-right animated flash" style={{ marginRight: 5 }}>
                <button onClick={this.submit} disabled={allUpdating} className="btn btn-primary">
                  Save &amp; Update
                </button>
              </div>
            }
            <ReactResizeDetector handleHeight onResize={this.onResizNavBar} />
          </nav>
        }
        <div className="ag-theme-alpine" style={{ height: viewableHeight, width: '100%' }}>
          <AgGridReact
            reactNext
            enableColResize
            enableSorting
            animateRows
            columnDefs={columnDefs}
            getRowNodeId={data => data._id}
            onGridReady={this.onGridReady}
            rowData={fields}
          />
        </div>
      </div>
    );
  }

  renderInstruction(orgType) {
    let opportunityObjectName = 'Opportunity';
    if (orgType === 'hubspot') {
      opportunityObjectName = 'deal';
    }
    return (
      <div className="well well-sm">
        <div>
          <p>
            Select custom fields from your <code>Opportunity</code> object in your <CRMInstanceName /> instance for opportunity type integrations.
          </p>
          <p>
            If you cannot find the field(s) you are looking for, you can also <Link to={`/setup/sync-settings/${opportunityObjectName}`}>add more custom fields to the sync</Link>.
          </p>
        </div>
      </div>
    );
  }


  render() {
    const { loading, loaded } = this.state;
    const { organization } = this.props;
    const isDemo = organization && organization.is_demo;
    const orgType = organization && organization.type;
    return (
      <div>
        <Helmet title="Manage Opportunity Type Integration" />
        <div className="container">
          
          {!isDemo && this.renderInstruction(orgType)}
          {loading && <ProgressBar />}
          {!loading && loaded && <div>
            {!isDemo && this.renderTable()}
            {isDemo && <div className="alert alert-warning">
              This is a demo organization, please switch back to the original organization to change the amound field mapping.
            </div>}
          </div>}
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    organization: getUserCurrentOrganization(state),
    viewableDimensions: getViewableDimensions(state),
    dealTypeFields: getAllDealTypeFields(state),
  };
}

DealTypePage.propTypes = {
  organization: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  viewableDimensions: PropTypes.object.isRequired,
  dealTypeFields: PropTypes.array.isRequired,
};

DealTypePage.contextTypes = {
  router: PropTypes.object,
};

export default connect(mapStateToProps)(DealTypePage);
